import React, { useState } from 'react';

// Material UI
import { Box, Button, Container, Link, Paper, TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

// Images
import logoDark from '../images/logo-dark.png';

// Utility
import ReactGA from 'react-ga';
import fetchExpresso from 'utility/fetchExpresso';

export default function ForgotPassword() {
    const [errorMessage, setErrorMessage] = useState(null);
    const [email, setEmail] = useState('');
    const [emailSentStatus, setStatus] = useState(false);

    function onEmailChange(e) {
        const value = e.target.value.slice(0, 50);
        setEmail(value);
        setErrorMessage(null);
    }

    function forgotPassword() {
        fetchExpresso(`/api/auths/forget_password`, {
            method: 'POST',
            body: { email },
        })
            .then((res) => res.json())
            .then((data) => {
                if (data.err === false) {
                    setStatus(true);
                    ReactGA.event({
                        category: 'Authentication',
                        action: 'Forgot Password',
                        label: 'Unauthenticated',
                    });
                } else {
                    setErrorMessage(data.message);
                }
            })
            .catch(() => {
                setErrorMessage('Please check your connection and try again later');
            });
    }

    function resendLink() {
        setStatus(false);
    }

    function onSubmit(e) {
        e.preventDefault();
        forgotPassword();
    }

    return (
        <Page>
            <Header>
                <Container maxWidth="lg">
                    <a href="https://frontdoorplus.com/">
                        <Logo alt="FrontDoor+ Logo" src={logoDark} />
                    </a>
                </Container>
            </Header>

            <Container maxWidth="lg" sx={{ flex: 1 }}>
                <Content>
                    <CardRoot elevation={0}>
                        {emailSentStatus ? (
                            <Form>
                                <CardTitle component="h2">Check your inbox</CardTitle>
                                <CardSubtitle>
                                    If this email address is connected to a FrontDoor+ account, we&apos;ll send a password
                                    reset link shortly.
                                </CardSubtitle>
                                <EmailValue>{email}</EmailValue>
                                <SecondaryButton variant="outlined" onClick={resendLink}>
                                    Resend Reset Link
                                </SecondaryButton>
                            </Form>
                        ) : (
                            <Form component="form" noValidate onSubmit={onSubmit}>
                                <CardTitle component="h2">Reset your password</CardTitle>
                                <CardSubtitle>
                                    Enter your email address and we&apos;ll send you a secure link to reset your password.
                                </CardSubtitle>

                                <Label component="label" htmlFor="email">
                                    Email address
                                </Label>
                                <AuthTextField
                                    id="email"
                                    error={Boolean(errorMessage)}
                                    required
                                    type="email"
                                    variant="outlined"
                                    value={email}
                                    onChange={onEmailChange}
                                    placeholder="you@organization.com"
                                    fullWidth
                                    inputProps={{ 'aria-label': 'Email address' }}
                                />

                                {errorMessage && <ErrorText>{errorMessage}</ErrorText>}

                                <PrimaryButton type="submit" variant="contained">
                                    Send Reset Link
                                </PrimaryButton>

                                <HelperText>
                                    Remembered your password?{' '}
                                    <InlineLink
                                        href={`${process.env.REACT_APP_PUBLIC_URL}/login`}
                                        underline="none"
                                    >
                                        Sign in
                                    </InlineLink>
                                </HelperText>
                            </Form>
                        )}
                    </CardRoot>
                </Content>
            </Container>

            <Footer>
                <Container maxWidth="lg">
                    <FooterInner>
                        <FooterText>&copy; 2026 FrontDoor+</FooterText>
                        <FooterLink href="https://frontdoorplus.com" underline="none">
                            frontdoorplus.com
                        </FooterLink>
                    </FooterInner>
                </Container>
            </Footer>
        </Page>
    );
}

const Page = styled(Box)({
    minHeight: '100vh',
    backgroundColor: '#0b1013',
    color: '#f4f7f8',
    display: 'flex',
    flexDirection: 'column',
});

const Header = styled('header')(({ theme }) => ({
    borderBottom: '1px solid #232b31',
    padding: theme.spacing(2.25, 0),
}));

const Logo = styled('img')({
    display: 'block',
    height: 32,
    maxWidth: '100%',
});

const Content = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: theme.spacing(7),
    paddingBottom: theme.spacing(8),
}));

const CardRoot = styled(Paper)(({ theme }) => ({
    width: '100%',
    maxWidth: 560,
    backgroundColor: '#151b20',
    border: '1px solid #29323a',
    borderRadius: 8,
    color: '#f4f7f8',
    padding: theme.spacing(4.5),
    [theme.breakpoints.down('md')]: {
        justifySelf: 'start',
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(3),
    },
}));

const Form = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
});

const CardTitle = styled(Typography)(({ theme }) => ({
    color: '#f8fafb',
    fontSize: 24,
    fontWeight: 800,
    lineHeight: 1.2,
    marginBottom: theme.spacing(1),
}));

const CardSubtitle = styled(Typography)(({ theme }) => ({
    color: '#a8b0b7',
    fontSize: 16,
    lineHeight: 1.4,
    marginBottom: theme.spacing(4),
}));

const Label = styled(Typography)(({ theme }) => ({
    color: '#e6eaed',
    fontSize: 15,
    fontWeight: 800,
    marginBottom: theme.spacing(1),
}));

const AuthTextField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    '& .MuiOutlinedInput-root': {
        color: '#f4f7f8',
        backgroundColor: '#0b1013',
        borderRadius: 4,
        '& fieldset': {
            borderColor: '#29323a',
        },
        '&:hover fieldset': {
            borderColor: '#3a454d',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#34cf91',
        },
        '&.Mui-error fieldset': {
            borderColor: '#f06d6d',
        },
    },
    '& .MuiOutlinedInput-input': {
        padding: theme.spacing(1.55, 1.75),
        fontSize: 16,
        '&::placeholder': {
            color: '#9fa8af',
            opacity: 1,
        },
    },
}));

const ErrorText = styled(Typography)(({ theme }) => ({
    color: '#ff8b8b',
    fontSize: 14,
    fontWeight: 700,
    marginTop: theme.spacing(-1.5),
    marginBottom: theme.spacing(2),
}));

const PrimaryButton = styled(Button)({
    backgroundColor: '#34cf91',
    borderRadius: 4,
    color: '#07100d',
    fontSize: 15,
    fontWeight: 800,
    minHeight: 50,
    textTransform: 'none',
    '&:hover': {
        backgroundColor: '#58dfaa',
    },
});

const SecondaryButton = styled(Button)({
    color: '#f4f7f8',
    borderColor: '#2d363d',
    borderRadius: 4,
    fontSize: 15,
    fontWeight: 700,
    minHeight: 48,
    textTransform: 'none',
    '&:hover': {
        borderColor: '#3c474f',
        backgroundColor: '#151b20',
    },
});

const EmailValue = styled(Typography)(({ theme }) => ({
    color: '#f8fafb',
    fontSize: 16,
    fontWeight: 800,
    marginTop: theme.spacing(-2),
    marginBottom: theme.spacing(3),
}));

const HelperText = styled(Typography)(({ theme }) => ({
    color: '#a8b0b7',
    fontSize: 15,
    marginTop: theme.spacing(2),
}));

const InlineLink = styled(Link)({
    color: '#34cf91',
    fontWeight: 700,
    '&:hover': {
        color: '#58dfaa',
    },
});

const Footer = styled(Box)(({ theme }) => ({
    borderTop: '1px solid #232b31',
    padding: theme.spacing(2.7, 0),
}));

const FooterInner = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
    },
}));

const FooterText = styled(Typography)({
    color: '#9fa8af',
    fontSize: 14,
});

const FooterLink = styled(Link)({
    color: '#9fa8af',
    fontSize: 14,
});
